import React from "react";
import { useEffect, useState } from "react";

import { PlusIcon, TrashIcon } from "@heroicons/react/outline";

import useMeta from "../../data/useMeta";
import LoadingWheel from "../Shared/LoadingWheel";
import ActionButton from "../Shared/ActionButton";
import ConfirmDelete from "../Shared/ConfirmDelete";
import IntegrationsModal from "./IntegrationsModal";
import BranchMappingModal from "./BranchMappingModal";
import useFacebookSdk from "../../hooks/useFacebookSdk";
import useIntegrations from "../../data/useIntegrations";
import ActionTextButton from "../Shared/ActionTextButton";
import { ButtonInput } from "./../Shared/Inputs/ButtonInput";
import { useAltoBranches } from "../../data/alto/useAltoBranches";
import { useApexBranches } from "../../data/apex/useApexBranches";
import ActionButtonContainer from "../Shared/ActionButtonContainer";
import { IIntegration } from "../../models/integration/Integration";
import { IMetaPageConfig } from "../../models/meta/MetaPageConfig";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";

interface IntegrationsListRowProps {
  integration: IIntegration;
  onDelete: (integrationId: string) => void;
  onUpdate: (integration: IIntegration) => void;
  onMapBranches?: () => void;
}

function IntegrationsListRow({
  integration,
  onDelete,
  onUpdate,
  onMapBranches,
}: IntegrationsListRowProps) {
  const [openUpdate, setOpenUpdate] = useState(false);

  const handleMapBranch = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setOpenUpdate(true);
  };

  return (
    <>
      {(integration.appName === "Apex27" || integration.appName === "Alto") && (
        <BranchMappingModal
          useCrmBranches={integration.appName === "Apex27" ? useApexBranches : useAltoBranches}
          open={openUpdate}
          setOpen={setOpenUpdate}
          integration={integration}
        />
      )}
      <li
        className="pl-3 pr-4 py-3 flex items-center justify-between text-sm hover:cursor-pointer hover:bg-gray-100"
        onClick={(e: React.MouseEvent<HTMLLIElement>) => {
          e.stopPropagation();
          e.preventDefault();
          onUpdate(integration);
        }}
      >
        <div className="w-0 flex-1 flex flex-col items-start md:flex-row md:items-center">
          <p className="flex-shrink-0 text-sm font-medium text-etpink-600 md:w-1/4">
            {integration.appName}
          </p>
        </div>

        <div className="ml-4 flex-shrink-0 flex items-center -mr-2">
          <ActionButtonContainer>
            {(integration.appName === "Apex27" ||
              integration.appName === "Alto") && (
                <ActionTextButton onClick={handleMapBranch} text="Map Branches" />
              )}
            <ActionButton
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                e.preventDefault();
                onDelete(integration.id);
              }}
              tooltip="Delete"
              Icon={TrashIcon}
            />
          </ActionButtonContainer>
        </div>
      </li>
    </>
  );
}

interface MetaPageConfigsListRowProps {
  pageConfig: IMetaPageConfig;
  setConfirmDeleteOpen: (open: boolean) => void;
  setDeleteId: (id: string | null) => void;
}

function MetaPageConfigsListRow({
  pageConfig,
  setConfirmDeleteOpen,
  setDeleteId,
}: MetaPageConfigsListRowProps) {
  const handleDeleteClick = (e: React.MouseEvent<HTMLButtonElement>, pageConfigId: string) => {
    e.stopPropagation();
    setDeleteId(pageConfigId);
    setConfirmDeleteOpen(true);
  };

  return (
    <li className="pl-3 pr-4 py-3 flex items-center justify-between text-sm hover:bg-gray-100">
      <div className="w-0 flex-1 flex flex-col items-start md:flex-row md:items-center">
        <p className="flex-shrink-0 text-sm font-medium text-etpink-600">
          {`${pageConfig.pageName} ${pageConfig.pageId !== pageConfig.instagramUserId ? `- Facebook Page ID: ${pageConfig.pageId}` : ""} ${pageConfig.instagramUserId ? ` - Instagram ID: ${pageConfig.instagramUserId}` : ""}`}
        </p>
      </div>
      <div className="ml-4 flex-shrink-0 flex items-center -mr-2">
        <ActionButtonContainer>
          <ActionButton
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleDeleteClick(e, pageConfig.id)}
            tooltip="Delete"
            Icon={TrashIcon}
          />
        </ActionButtonContainer>
      </div>
    </li>
  );
}

interface IntegrationSettingsPartialProps {
  agencyId: string;
}

export default function IntegrationsSettingsPartial({ agencyId }: IntegrationSettingsPartialProps) {
  const { integrations, add, update, remove } = useIntegrations(agencyId);
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers(
    "Integration configuration"
  );
  const { initFacebookSdk, getFacebookLoginStatus, fbLogin } = useFacebookSdk();
  const { metaPageConfigs, remove: metaRemove, setup } = useMeta(agencyId);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmMetaDeleteOpen, setConfirmMetaDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string>();
  const [deleteMetaId, setDeleteMetaId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [integration, setIntegration] = useState<IIntegration>();
  const [isUpdate, setIsUpdate] = useState(false);

  const handleAddIntegration = (addedIntegration: IIntegration) => {
    // @ts-ignore - react-query mutation types from JS file
    add.mutate(addedIntegration, saveHandlers);
  };

  const handleRemoveIntegration = () => {
    if (!deleteId) return;

    // @ts-ignore - react-query mutation types from JS file
    remove.mutate(deleteId, deleteHandlers);
    setConfirmDeleteOpen(false);
  };

  const handleUpdateIntegration = (updatedIntegration: IIntegration) => {
    // @ts-ignore - react-query mutation types from JS file
    update.mutate(updatedIntegration, saveHandlers);
  };

  const handleAdd = () => {
    setIsUpdate(false);
    setOpen(true);
  };

  const handleRemoveMetaPageConfig = () => {
    if (!deleteMetaId) return;
    // @ts-ignore - react-query mutation types from JS file
    metaRemove.mutate(deleteMetaId, deleteHandlers);
    setConfirmMetaDeleteOpen(false);
  };

  const handleFacebookLogin = () => {
    fbLogin().then((response: any) => {
      console.log(response);
      if (response.status === "connected") {
        console.log("Person is connected");
        //response contains user token
        // @ts-ignore - react-query mutation types from JS file
        setup.mutate(response.authResponse, saveHandlers);
      } else {
        console.log("Person is not connected");
      }
    });
  };

  function openInstagramPopup() {
    // Instagram-only OAuth flow
    // Redirect to backend API endpoint (same pattern as Google OAuth)
    const redirectUri =
      // "https://dashboard-api.estatetrack.co.uk/public/oauth/instagram-callback";
      "https://localhost:7259/public/oauth/instagram-callback";
    const state = encodeURIComponent(
      `agencyId=${agencyId},source=instagram,location=${encodeURIComponent(window.location.href)}`
    );

    // Use Instagram App ID - you may need to configure this separately from Facebook App ID
    // For now, using the same app ID but you should configure Meta:InstagramAppId in backend
    const instagramAppId = "618520274545252"; // This should be your Instagram App ID from Meta App Dashboard

    const authUrl = [
      "https://api.instagram.com/oauth/authorize",
      `?client_id=${instagramAppId}`,
      `&redirect_uri=${encodeURIComponent(redirectUri)}`,
      "&scope=instagram_business_basic",
      "&response_type=code",
      `&state=${state}`,
    ].join("");

    console.log("Instagram auth URL: ", authUrl);

    const width = 450;
    const height = 600;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
      authUrl,
      "InstagramLogin",
      `width=${width},height=${height},top=${top},left=${left}`
    );
  }

  async function fetchUserPagesAndIGAccount(userAccessToken: string) {
    // 1) List all Pages they manage
    const pagesRes = await fetch(
      `https://graph.facebook.com/v19.0/me/accounts?access_token=${userAccessToken}`
    );
    const pages = (await pagesRes.json()).data;

    // 2) For each page, ask for instagram_business_account
    for (let page of pages) {
      const pageInfoRes = await fetch(
        `https://graph.facebook.com/v19.0/${page.id}` +
        `?fields=instagram_business_account` +
        `&access_token=${page.access_token}`
      );
      const pageInfo = await pageInfoRes.json();

      if (pageInfo.instagram_business_account?.id) {
        console.log(
          "Found IG Business account!",
          pageInfo.instagram_business_account
        );
        //Here's the IG account ID
        return pageInfo.instagram_business_account;
      }
    }

    // If we get here, no IG business/creator account was attached
    alert(
      "No Instagram Business or Creator account found on any of your Facebook Pages."
    );
    return null;
  }

  const handleGoogleLogin = () => {
    const clientId =
      "156965414692-buja5v0dvo4t6i99i1up8u9n4a1gq1m6.apps.googleusercontent.com"; // Replace with your actual Client ID
    const redirectUri =
      "https://dashboard-api.estatetrack.co.uk/public/oauth/callback"; // Replace with your actual redirect URI

    // Include the new Google My Business scope
    const scope = [
      "https://www.googleapis.com/auth/userinfo.email",
      "https://www.googleapis.com/auth/userinfo.profile",
      "https://www.googleapis.com/auth/business.manage", // New scope
    ].join(" "); // Join scopes with space for the OAuth URL

    const responseType = "code"; // Request an authorization code
    const state = encodeURIComponent(
      `agencyId=${agencyId},source=google,location=${window.location.href}`
    );

    // Add &access_type=offline and &prompt=consent
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth
    ?client_id=${clientId}
    &redirect_uri=${encodeURIComponent(redirectUri)}
    &scope=${encodeURIComponent(scope)}
    &response_type=${responseType}
    &state=${state}
    &access_type=offline
    &prompt=consent`.replace(/\s+/g, "");

    // Open Google's OAuth screen in a new tab instead of redirecting current page
    // Using window.open to preserve current app session and prevent navigation away
    try {
      window.open(googleAuthUrl, "_blank", "noopener,noreferrer");
    } catch (e) {
      // Fallback to navigating current window if popup blocked
      window.location.href = googleAuthUrl;
    }
  };

  useEffect(() => {
    initFacebookSdk().then(() => {
      getFacebookLoginStatus().then((response) => {
        if (response.authResponse == null) {
          console.log("No login status for the person");
        } else {
          console.log(response);
        }
      });
    });

    // Check for Instagram OAuth callback results
    const urlParams = new URLSearchParams(window.location.search);
    const instagramSuccess = urlParams.get("instagram_success");
    const instagramError = urlParams.get("instagram_error");

    if (instagramSuccess === "true") {
      // Remove query params and reload to refresh meta page configs
      window.history.replaceState({}, document.title, window.location.pathname);
      window.location.reload();
    } else if (instagramError) {
      // Remove query params and show error
      window.history.replaceState({}, document.title, window.location.pathname);
      alert("Failed to connect Instagram: " + decodeURIComponent(instagramError));
    }
  }, []);

  if (integrations.isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <LoadingWheel width="w-12" height="h-12" />
      </div>
    );
  } else {
    return (
      <>
        <ConfirmDelete
          itemName="Portal Qualifier Configuration"
          message="Are you sure you want to delete this Portal Qualifier Configuration? This action cannot be undone."
          open={confirmDeleteOpen}
          setOpen={setConfirmDeleteOpen}
          onConfirm={handleRemoveIntegration}
        />
        <ConfirmDelete
          itemName="Meta Page Configuration"
          message="Are you sure you want to delete this Meta Page Configuration? This action cannot be undone."
          open={confirmMetaDeleteOpen}
          setOpen={setConfirmMetaDeleteOpen}
          onConfirm={handleRemoveMetaPageConfig}
        />
        <IntegrationsModal
          open={open}
          setOpen={setOpen}
          integration={integration}
          isUpdate={isUpdate}
          handleAddIntegration={handleAddIntegration}
          handleUpdateIntegration={handleUpdateIntegration}
        />

        <div className="">
          <div className="-mx-6 px-6">
            <h3 className="text-xl font-medium text-gray-900">
              Integration Settings
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Settings for managing your integrations
            </p>
          </div>
          <button
            type="button"
            onClick={handleAdd}
            className="my-6 flex items-center justify-center w-full md:w-auto px-3 py-2 mr-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-etpink-600 hover:bg-etpink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-etpink-500"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Add Integration
          </button>
          <ul className="my-6 border border-gray-200 bg-white rounded-md divide-y divide-gray-200">
            {integrations.isLoading || metaPageConfigs.isLoading ? (
              <li className="py-4 flex justify-center">
                <LoadingWheel width="w-8" height="h-8" />
              </li>
            ) : integrations.data?.length > 0 ||
              metaPageConfigs.data?.data?.length > 0 ? (
              <>
                <div className="divide-y divide-gray-200">
                  {integrations.data?.map((integration: IIntegration) => (
                    <IntegrationsListRow
                      key={`integration-${integration.id}`}
                      integration={integration}
                      onDelete={(integrationId) => {
                        setDeleteId(integrationId);
                        setConfirmDeleteOpen(true);
                      }}
                      onUpdate={(integration) => {
                        setIsUpdate(true);
                        setIntegration(integration);
                        setOpen(true);
                      }}
                    />
                  ))}
                </div>
                <div className="divide-y divide-gray-200">
                  {metaPageConfigs.data?.data?.map((metaPageConfig: IMetaPageConfig) => (
                    <MetaPageConfigsListRow
                      pageConfig={metaPageConfig}
                      key={metaPageConfig.id}
                      setConfirmDeleteOpen={setConfirmMetaDeleteOpen}
                      setDeleteId={setDeleteMetaId}
                    />
                  ))}
                </div>
              </>
            ) : (
              <li>
                <div className="py-5 text-center text-sm text-gray-400">
                  No Integrations Configured
                </div>
              </li>
            )}
          </ul>

          <div className="flex gap-4">
            {/* @ts-ignore */}
            <ButtonInput
              label="Login with Facebook"
              onClick={() => handleFacebookLogin()}
              isSubmit={false}
            />
            {/* @ts-ignore */}
            <ButtonInput
              label="Login with Only Instagram"
              onClick={() => openInstagramPopup()}
              isSubmit={false}
            />
            {/* @ts-ignore */}
            <ButtonInput
              label="Login with Google"
              onClick={() => handleGoogleLogin()}
              isSubmit={false}
            />
          </div>

          {/* <ButtonInput label="Re Request Facebook Permissions" onClick={() => handleReRequestPermissions()} isSubmit={false} /> */}
        </div>
      </>
    );
  }
}
